<template>
  <div class="public-transport">
    <pt-select-modal
      :map="mapgl"
      :base-controller="BaseController"
      :editor-controller="EditorController"
      v-if="!mode"
    />
    <div class="public-transport-layout">
      <r-main-panel
        v-if="mode"
        :title="panelHeaderTitle"
        :subheader="model.name"
        :icon="panelHeaderIcon"
      >
        <pt-editor
          :map="mapgl"
          :state="editorState"
          :mode="mode"
          :controller="EditorController"
        />
      </r-main-panel>
      <div id="public-transport-map" class="public-transport-map">
        <div v-if="overlay" class="public-transport-overlay" />
        <div class="public-transport-controls" v-if="mode">
          <el-button
            class="r-button"
            size="mini"
            type="info"
            @click="backToModeSelect"
            :disabled="layersLoading"
          >
            <i class="el-icon-arrow-left" />
            К списку моделей
          </el-button>
          <pt-mode-switcher
            :state="editorState"
            :mode="mode"
            @change-mode="changeMode"
          />
        </div>
        <pt-rev-switcher
          v-if="mode === 'line_routes' && activeObjectId"
          :state="editorState"
          :draw="draw"
          :map="mapgl"
          :rev="activeRouteReverse"
          :reverse-id="activeRouteReverseId"
        />
        <div v-if="layersLoading" class="pt-loader">
          <i class="el-icon-loading" />
          Загрузка данных...
        </div>
        <div class="public-transport-map-actions">
          <map-tools
            :map="mapgl"
            :is-traffic-counts="true"
            @change-prop="changeProp"
            :map-bearing="mapBearing"
            :is3d="is3d"
          />
          <pt-legend
            v-if="mode"
            :is-legend-active="isLegendActive"
            @toggle-legend="toggleLegend"
          />
        </div>
      </div>
      <r-info-panel
        v-if="
          (mode === 'line_routes' && editorState.mode === 'edit') || activeCard
        "
        :width="320"
      >
        <pt-routes
          v-if="
            mode === 'line_routes' && editorState.mode === 'edit' && !activeCard
          "
        />
        <pt-active-card
          v-else-if="activeCard"
          :draw="draw"
          :mapgl="mapgl"
          :state="editorState"
          :mode="mode"
        />
      </r-info-panel>
    </div>
  </div>
</template>

<script>
import cloneDeep from "lodash.clonedeep";
import extent from "turf-extent";
import MapTools from "../map/components/map-tools/map-tools";
import PtSelectModal from "./components/pt-select-modal";
import PtModeSwitcher from "./components/pt-mode-switcher";
import PtRevSwitcher from "./components/routes/pt-rev-switcher";
import PtLegend from "./components/pt-legend";
import PtEditor from "./components/pt-editor";
import PtRoutes from "./components/routes/pt-routes";
import PtActiveCard from "./components/active-card/pt-active-card";
import { initMap } from "./core";
import { initialEditorState } from "./configs";

export default {
  components: {
    MapTools,
    PtSelectModal,
    PtModeSwitcher,
    PtRevSwitcher,
    PtLegend,
    PtEditor,
    PtRoutes,
    PtActiveCard
  },
  data() {
    return {
      mapgl: null,
      draw: null,
      mapLoading: null,
      mapBearing: null,
      is3d: false,
      BaseController: null,
      EditorController: null,
      isLegendActive: true,
      editorState: cloneDeep(initialEditorState)
    };
  },
  watch: {
    "$store.state.publicTransport.mode": function() {
      this.$nextTick(() => this.mapgl.resize());
    },
    "$store.state.theme": function(val, prev) {
      this.mapgl.setLayoutProperty(prev, "visibility", "none");
      this.mapgl.setLayoutProperty(val, "visibility", "visible");
    },
    "$store.state.publicTransport.activeCard": function(val) {
      this.$nextTick(() => this.mapgl.resize({ noRequest: !!val }));
    },
    "$store.state.publicTransport.flyToRoute": function(route) {
      if (!route) return;

      const { geom, reverse_route } = route;
      if (!geom) return;

      const routeFeature = {
        type: "Feature",
        geometry: geom
      };
      const reverseFeature = {
        type: "Feature",
        geometry: reverse_route.geom
      };

      const bounds = extent({
        type: "FeatureCollection",
        features: [routeFeature, reverseFeature]
      });

      this.mapgl.fitBounds(bounds, {
        padding: 20
      });
      this.$store.commit("FLY_TO_ROUTE", null);
    },
    "editorState.mode": function() {
      this.$nextTick(() => this.mapgl.resize({ noRequest: true }));
    }
  },
  computed: {
    mode() {
      return this.$store.state.publicTransport.mode;
    },
    model() {
      return this.$store.state.publicTransport.model;
    },
    activeCard() {
      return this.$store.state.publicTransport.activeCard;
    },
    overlay() {
      return this.$store.state.publicTransport.overlay;
    },
    panelHeaderTitle() {
      return this.mode === "stop_points" ? "Остановочные пункты" : "Маршруты";
    },
    panelHeaderIcon() {
      return this.mode === "stop_points" ? "bus" : "line-geom";
    },
    layersLoading() {
      return this.$store.state.publicTransport.loadingLayers.length > 0;
    },
    activeObjectId() {
      return this.$store.state.publicTransport.activeCardId;
    },
    activeRouteReverse() {
      return this.$store.state.publicTransport.activeRoute.reverse;
    },
    activeRouteReverseId() {
      return this.$store.state.publicTransport.activeRoute.reverseRouteId;
    }
  },
  methods: {
    saveMapParams() {
      const zoom = this.mapgl.getZoom();
      const center = this.mapgl.getCenter();

      localStorage.setItem("ptMapCenter", JSON.stringify(center));
      localStorage.setItem("ptMapZoom", JSON.stringify(zoom));
    },
    changeProp({ field, value }) {
      this[field] = value;
    },
    toggleLegend() {
      this.isLegendActive = !this.isLegendActive;
    },
    getObjectsLimit() {
      return 10000;
    },
    backToModeSelect() {
      if (this.layersLoading) return;
      const { ids } = this.$store.state.publicTransport.model;
      this.BaseController.toggleOff(ids);
      this.EditorController.toggleEditorOff();

      this.$store.commit("SET_PT_STATE_PROP", {
        name: "mode",
        value: null
      });
    },
    changeMode(value) {
      if (this.layersLoading) return;
      this.$store.commit("SET_PT_STATE_PROP", {
        name: "mode",
        value
      });

      this.EditorController.toggleLayerMode(value);
    }
  },
  mounted() {
    initMap(this);
  },
  beforeDestroy() {
    window.removeEventListener("beforeunload", this.saveMapParams);
    this.$store.commit("CLEAR_PT_MODEL");
    this.$store.commit("SET_PT_STATE_PROP", {
      name: "activeCard",
      value: false
    });
    this.backToModeSelect();
    this.saveMapParams();
  }
};
</script>

<style lang="scss">
.public-transport {
  height: 100%;
  width: 100%;
  display: flex;
  position: relative;
  background-color: var(--bg_surface) !important;

  &-layout {
    width: 100%;
    height: 100%;
    display: flex;
  }

  &-map {
    height: 100%;
    width: 100%;
  }

  &-overlay {
    position: absolute;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    z-index: 1000;
  }

  &-controls {
    position: absolute;
    left: 16px;
    top: 16px;
    z-index: 100;
    display: flex;

    .pt-mode-switcher {
      margin-left: 16px;
    }
  }

  &-map-actions {
    position: absolute;
    right: 0;
    top: 0;
    z-index: 100;
  }

  // toggle off mapbox focus and controls
  .mapboxgl-canvas {
    &:focus {
      outline: none;
    }
  }
  .mapboxgl-ctrl-top-right {
    display: none;
  }
}

.pt-loader {
  position: absolute;
  bottom: 16px;
  left: calc(50%);
  transform: translateX(-50%);
  font-size: 12px;
  line-height: 18px;
  padding: 10px 20px;
  border-radius: var(--border-radius);
  border: 1px solid transparent;
  border-color: var(--dividers_low_contrast) !important;
  background-color: var(--bg_panel_primary) !important;
  display: flex;
  align-items: center;
  color: var(--accent_primary) !important;
  z-index: 100;

  i {
    font-size: 18px;
    line-height: 18px;
    margin-right: 5px;
  }
}
</style>
